/**
 * Build PolicyDTO for quotations-api policy init (same shape as agent AgentSale).
 * GIS policy creation runs after payment via PolicyService.createPolicyOnGIS.
 */

import { MOTOR_PROD_ID } from '@/constants/motorPrivate';
import { formatDate, changeFormat } from '@/utils/time';

function formatPolicyCoverFrom(coverFrom) {
  if (!coverFrom) return changeFormat(formatDate(new Date().toISOString().slice(0, 10)));
  if (typeof coverFrom === 'string' && coverFrom.includes('/')) return coverFrom;
  if (typeof coverFrom === 'string' && coverFrom.includes('-')) return changeFormat(coverFrom);
  return changeFormat(formatDate(String(coverFrom)));
}

function mapPolicyBenefits(additionalBenefits) {
  if (!Array.isArray(additionalBenefits) || !additionalBenefits.length) return undefined;
  const benefits = additionalBenefits
    .map((b) => ({
      sectCode: Number(b.sectCode || b.sectionCode || b.id) || null,
      noOfBeneficiaries: Number(b.noOfBeneficiaries) || 0,
    }))
    .filter((b) => b.sectCode);
  return benefits.length ? benefits : undefined;
}

function normalizeLogbookTransferred(car, vehicle) {
  if (car.logbookTransferred === 'Y' || car.logbookTransferred === 'N') {
    return car.logbookTransferred;
  }
  const raw = vehicle.logbookTransferred || car.logbookTransferred;
  if (raw === 'yes') return 'Y';
  if (raw === 'no') return 'N';
  return raw || '';
}

/**
 * @returns {object} quotations-api PolicyDTO
 */
export default function buildMotorQuotationPolicyDto({
  quoteId,
  activeRisk = {},
  riskId,
  form = {},
  vehicle = {},
  branchCode,
  coverFrom,
  totalPremium = 0,
  prodId = MOTOR_PROD_ID,
  agentId = 0,
}) {
  const car = { ...(activeRisk?.car || {}), ...vehicle };
  const reg = (car.registrationNumber || '').toUpperCase();
  const logbookTransferred = normalizeLogbookTransferred(car, vehicle);
  const carDTO = {
    carId: car.carId || 0,
    riskId: Number(riskId),
    covertypeId: Number(activeRisk?.covertypeId || activeRisk?.coverTypeID) || 0,
    registrationNumber: reg,
    make: car.make || '',
    model: car.model || '',
    yearOfManufacture: car.yearOfManufacture ? Number(car.yearOfManufacture) : undefined,
    cc: car.cc != null && car.cc !== '' ? String(car.cc) : '',
    chassisNumber: (car.chassisNumber || '').toUpperCase(),
    engineNo: (car.engineNumber || car.engineNo || '').toUpperCase(),
    color: (car.color || '').toUpperCase(),
    logBookNumber: car.logBookNumber || car.logbookNumber || '',
    logBookImageUrl: car.logBookImageUrl || '',
    seatingCapacity: car.seatingCapacity != null ? Number(car.seatingCapacity) : 0,
    valueOfCar: Number(car.valueOfCar ?? car.value ?? 0),
    bodyType: car.bodyType || '',
    logbookTransferred,
    valuer: car.valuer || '',
    valuationLocation: car.valuationLocation || '',
    valuationDate: car.valuationDate || undefined,
  };

  const benefits = mapPolicyBenefits(activeRisk?.additionalBenefits);
  if (benefits) carDTO.benefits = benefits;

  return {
    quoteId: Number(quoteId),
    prodId: Number(prodId),
    branchCode: branchCode != null ? String(branchCode) : '10',
    coverFrom: formatPolicyCoverFrom(coverFrom),
    dateFormat: 'dd/MM/yyyy',
    premium: Number(totalPremium) || 0,
    fullName: form.fullName || '',
    email: form.email || '',
    mobileNumber: form.mobileNumber || '',
    pinNumber: form.kraPinNumber || form.pinNumber || '',
    agentId: String(agentId != null ? agentId : 0),
    dlImage: {
      backImageUrl: '',
      frontImageUrl: '',
    },
    carDTOS: [carDTO],
  };
}
